import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import KosyamLogo from "@/components/KosyamLogo";
import LanguageToggle from "@/components/LanguageToggle";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";
import { GraduationCap, Eye, EyeSlash } from "@phosphor-icons/react";

const DEMO_KEYS = [
  { key: "director", email: "directeur@demo.bf" },
  { key: "accountant", email: "comptable@demo.bf" },
  { key: "teacher", email: "enseignant@demo.bf" },
  { key: "parent", email: "parent@demo.bf" },
  { key: "student", email: "eleve@demo.bf" },
  { key: "director", email: "recteur@upa-dakar.sn", labelKey: "director", extra: "🎓 Uni Dakar" },
  { key: "director", email: "directeur@faso-metiers.bf", labelKey: "director", extra: "🔧 CFP Métiers" },
];

function _routeFor(role) {
  if (role === "parent") return "/portail";
  if (role === "student") return "/app/eleve";
  return "/app";
}

export default function Login() {
  const { t } = useTranslation();
  const { user, login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    if (user && user !== false) nav(_routeFor(user.role), { replace: true });
  }, [user, nav]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email.trim().toLowerCase(), password);
      toast.success(t("auth.welcome_toast", { name: u.name }));
      nav(_routeFor(u.role), { replace: true });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const fillDemo = (dEmail) => {
    setEmail(dEmail);
    setPassword("Demo2026!");
  };

  return (
    <div className="min-h-screen bg-background text-foreground grid lg:grid-cols-2">
      <div className="hidden lg:block relative overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1548102245-c79dbcfa9f92?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwyfHxhZnJpY2FuJTIwc2Nob29sJTIwY2hpbGRyZW4lMjBzbWlsaW5nfGVufDB8fHx8MTc4NjA3MjgxMHww&ixlib=rb-4.1.0&q=85"
          alt="Kosyam" className="w-full h-full object-cover" loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background/95 via-background/40 to-transparent" />
        <div className="absolute bottom-10 left-10 right-10">
          <div className="inline-flex items-center gap-2 bg-card/90 backdrop-blur px-4 py-2 rounded-full border border-border">
            <GraduationCap size={18} weight="duotone" className="text-accent" />
            <span className="text-xs font-semibold uppercase tracking-widest">{t("app.brand.tagline_short")}</span>
          </div>
          <h2 className="mt-6 font-heading text-4xl font-bold leading-tight text-foreground">
            {t("auth.hero_slogan_line1")}<br />{t("auth.hero_slogan_line2")}
          </h2>
          <p className="mt-3 text-muted-foreground text-sm max-w-md">
            {t("auth.hero_slogan_desc")}
          </p>
        </div>
      </div>

      <div className="relative flex flex-col items-center justify-center px-4 sm:px-6 py-10 sm:py-12">
        <div className="absolute top-4 right-4 sm:top-6 sm:right-6"><LanguageToggle /></div>
        <div className="w-full max-w-md">
          <Link to="/" className="flex items-center gap-4 mb-8 sm:mb-10" data-testid="link-home">
            <div className="w-14 h-14 rounded-xl overflow-hidden text-primary shrink-0 flex items-center justify-center">
              <KosyamLogo size={56} />
            </div>
            <div className="flex flex-col justify-center min-h-[56px]">
              <div className="font-heading font-bold text-xl leading-tight">Kosyam</div>
              <div className="text-[10px] text-muted-foreground uppercase tracking-widest mt-1">{t("app.brand.tagline_short")}</div>
            </div>
          </Link>

          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">{t("auth.welcome_back")}</h1>
          <p className="text-sm text-muted-foreground mt-2">{t("auth.sign_in_subtitle")}</p>

          <form onSubmit={submit} className="mt-6 sm:mt-8 space-y-4">
            <div>
              <Label htmlFor="email">{t("auth.email")}</Label>
              <Input
                id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder={t("auth.email_placeholder")} className="mt-1.5" data-testid="input-email"
              />
            </div>
            <div>
              <Label htmlFor="password">{t("auth.password")}</Label>
              <div className="relative">
                <Input
                  id="password" type={showPwd ? "text" : "password"} required
                  value={password} onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••" className="mt-1.5 pr-10" data-testid="input-password"
                />
                <button
                  type="button" onClick={() => setShowPwd(s => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  data-testid="btn-toggle-password"
                  aria-label={showPwd ? t("common.close") : t("auth.password")}
                >
                  {showPwd ? <EyeSlash size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <Button
              type="submit" disabled={loading}
              className="w-full bg-accent hover:bg-accent/90 text-accent-foreground rounded-full font-semibold h-11"
              data-testid="btn-submit-login"
            >
              {loading ? t("auth.signing_in") : t("auth.login")}
            </Button>
          </form>

          <div className="mt-8">
            <div className="text-xs uppercase tracking-widest text-muted-foreground mb-3">{t("auth.demo_accounts")}</div>
            <div className="grid grid-cols-2 gap-2">
              {DEMO_KEYS.map((d) => (
                <button
                  key={d.email} type="button" onClick={() => fillDemo(d.email)}
                  className="text-left border border-border rounded-md px-3 py-2 hover:border-accent hover:bg-accent/5 transition-colors"
                  data-testid={`btn-demo-${d.email.replace(/[^a-z0-9]/gi,'-')}`}
                >
                  <div className="text-xs font-semibold">{d.extra || t(`roles.${d.key}`)}</div>
                  <div className="text-[10px] text-muted-foreground truncate">{d.email}</div>
                </button>
              ))}
            </div>
            <p className="text-[10px] text-muted-foreground mt-3">
              {t("auth.demo_password_hint", { pwd: "Demo2026!", admin: "hiramgildas@gmail.com / Scolarite2026!" })}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
